<template>
  <div>
    <v-divider class="my-6"></v-divider>
    <v-card-title class="pa-0 mb-4">
      <v-icon start>mdi-file-document-multiple</v-icon>
      {{ t(strings.fileUploadsTitle) }}
    </v-card-title>

    <!-- File Categories -->
    <v-card
      v-for="category in fileCategories"
      :key="category.key"
      variant="outlined"
      class="mb-4"
    >
      <v-card-title class="text-subtitle-1">
        <v-icon start>{{ category.icon }}</v-icon>
        {{ t(category.titleKey) }}
      </v-card-title>
      <v-card-text>
        <div v-if="filesLoading">
          <v-skeleton-loader type="card" class="mb-2"></v-skeleton-loader>
          <v-skeleton-loader type="card"></v-skeleton-loader>
        </div>
        <PartFileUploader
          v-else
          :part-id="partId"
          :category="category.key"
          :existing-files="existingFiles"
          @file-uploaded="onFileUploaded"
          @file-deleted="onFileDeleted"
        />
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { part_form_strings as strings } from "./PartForm.strings.ts";
import { useReverseT } from "../../i18n.ts";
import PartFileUploader from "../part-file-uploader/PartFileUploader.vue";
import type { PartFile } from "@vendata/power-up-spec";
import { computed } from "vue";

type FileCategory =
  | "pedigree"
  | "shop_release"
  | "flow_test_report"
  | "dimensional_inspection"
  | "nde_report"
  | "photo";

interface FileCategoryConfig {
  key: FileCategory;
  titleKey: string;
  icon: string;
}

const { t } = useReverseT();

// Props
defineProps<{
  partId: string;
  existingFiles: PartFile[];
  filesLoading: boolean;
}>();

// Emits
const emit = defineEmits<{
  "file-uploaded": [file: PartFile];
  "file-deleted": [file: PartFile];
}>();

// File categories configuration
const fileCategories = computed((): FileCategoryConfig[] => [
  {
    key: "pedigree",
    titleKey: strings.pedigreeFilesTitle,
    icon: "mdi-certificate",
  },
  {
    key: "shop_release",
    titleKey: strings.shopReleaseFilesTitle,
    icon: "mdi-file-check",
  },
  {
    key: "flow_test_report",
    titleKey: strings.flowTestFilesTitle,
    icon: "mdi-test-tube",
  },
  {
    key: "dimensional_inspection",
    titleKey: strings.dimensionalInspectionFilesTitle,
    icon: "mdi-ruler",
  },
  {
    key: "nde_report",
    titleKey: strings.ndeReportFilesTitle,
    icon: "mdi-microscope",
  },
  { key: "photo", titleKey: strings.photoFilesTitle, icon: "mdi-camera" },
]);

// Event handlers
const onFileUploaded = (file: PartFile) => {
  emit("file-uploaded", file);
};

const onFileDeleted = (file: PartFile) => {
  emit("file-deleted", file);
};
</script>
